import {
    formatInvestmentProfileValue,
    IPQ_ENUMS,
    IPQ_FIELDS,
    IPQ_QUESTIONS,
    type IPQField,
    type UserInvestmentProfile,
} from '@fruitful/investment-management';
import { useEffect, useMemo, useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    NavigationRow,
    RowList,
    ScreenContainer,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { useGetUserInvestmentProfile } from '../hooks/useGetUserInvestmentProfile';
import useUpsertUserInvestmentProfile from '../hooks/useUpsertUserInvestmentProfile';
import { InvestmentProfileQuestionStep } from './InvestmentProfileQuestionStep';

interface RiskSequenceProps {
    onBack: () => void;
    onComplete: () => void;
}

const OVERVIEW_KEY = 'OVERVIEW';
type StepKey = IPQField | typeof OVERVIEW_KEY;

const STEP_SEQUENCE: readonly StepKey[] = [...IPQ_FIELDS, OVERVIEW_KEY];
const OVERVIEW_INDEX = STEP_SEQUENCE.indexOf(OVERVIEW_KEY);
const SEQUENCE_TITLE = 'Investment questionnaire';

function getQuestionLabel(field: IPQField): string {
    return IPQ_QUESTIONS.find((q) => q.key === field)?.label ?? field;
}

export const RiskSequence = ({ onBack, onComplete }: RiskSequenceProps) => {
    useTrackViewOnFocus('Profile/RiskSequence');
    const user = useAuthedUser();
    const [index, setIndex] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [profile, setProfile] = useState<Partial<UserInvestmentProfile>>({});

    const { data: existingProfile, isLoading: isLoadingProfile } =
        useGetUserInvestmentProfile({ userId: user.id });
    const { mutateAsync: upsertProfile, isPending: isUpdatingProfile } =
        useUpsertUserInvestmentProfile();

    const routes = STEP_SEQUENCE.map((key) => ({ key }));

    const checkCompleteness = (data: Partial<UserInvestmentProfile>) =>
        IPQ_FIELDS.every((field) => data[field] != null);

    const onOptionSelect = async (updates: Partial<UserInvestmentProfile>) => {
        await upsertProfile({
            body: updates,
            urlParams: {
                userId: user.id,
            },
        });

        setProfile({
            ...profile,
            ...updates,
        });

        if (isComplete) {
            setIndex(OVERVIEW_INDEX);
        } else {
            setIndex(index + 1);
        }
    };

    const handleBack = () => {
        if (index === 0 || isOnOverview) {
            onBack();
        } else {
            setIndex(index - 1);
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: StepKey } }) => {
        const stepKey = route.key;

        if (stepKey === OVERVIEW_KEY) {
            return (
                <YStack
                    paddingVertical={16}
                    gap={24}
                    justifyContent="space-between"
                >
                    <RowList>
                        {IPQ_FIELDS.map((field, stepIndex) => {
                            const fieldValue = profile[field];
                            const displayValue = formatInvestmentProfileValue(
                                field,
                                fieldValue ?? null,
                            );

                            return (
                                <NavigationRow
                                    key={field}
                                    title={getQuestionLabel(field)}
                                    subtitle={displayValue}
                                    titleProps={{
                                        numberOfLines: 2,
                                        ellipsizeMode: 'tail',
                                    }}
                                    onPress={() => {
                                        setIndex(stepIndex);
                                    }}
                                />
                            );
                        })}
                    </RowList>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Done"
                        onPress={onComplete}
                    />
                </YStack>
            );
        }

        const field: IPQField = stepKey;
        return (
            <InvestmentProfileQuestionStep
                key={profile[field]}
                question={getQuestionLabel(field)}
                options={IPQ_ENUMS[field]}
                initialValue={profile[field]}
                field={field}
                onSelect={onOptionSelect}
                isPending={isUpdatingProfile}
            />
        );
    };

    const isComplete = useMemo(() => checkCompleteness(profile), [profile]);
    const currentStep: StepKey = STEP_SEQUENCE[index] ?? IPQ_FIELDS[0];
    const isOnOverview = currentStep === OVERVIEW_KEY;

    useEffect(() => {
        if (!isLoadingProfile) {
            if (existingProfile) {
                if (checkCompleteness(existingProfile)) {
                    setIndex(OVERVIEW_INDEX);
                }
                setProfile(existingProfile);
            }
            setIsReady(true);
        }
    }, [isLoadingProfile, existingProfile]);

    if (!isReady) return <FullScreenSpinner />;

    return (
        <ScreenContainer
            title={SEQUENCE_TITLE}
            hasBackButton
            onBack={handleBack}
            containerProps={{ backgroundColor: '$white' }}
        >
            <View height="100%">
                <TabView
                    animationEnabled={!isComplete}
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    renderTabBar={() => null}
                    onIndexChange={setIndex}
                />
            </View>
        </ScreenContainer>
    );
};
